import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Banner, Button, Field, Input, Modal } from '../components/ui';
import { useSession } from '../lib/session';
import { membershipTarget, type ParcelMembership } from './model';
import { parcelApi } from './service';

type Source = { id: string; parcelId: string | null; county?: string | null; graphParcelId?: string | null } &
  ({ kind: 'property'; state: string } | { kind: 'job'; contractorRole: string });

export default function ParcelProvision({ source }: { source: Source }) {
  const { contractor } = useSession();
  return contractor ? <Provision key={`${contractor.id}/${source.kind}/${source.id}`} source={source} /> : null;
}
function Provision({ source }: { source: Source }) {
  const { contractor } = useSession();
  const [open, setOpen] = useState(false);
  const [county, setCounty] = useState('');
  const [declared, setDeclared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [destination, setDestination] = useState<string | null>(null);
  if (destination) return <Navigate to={destination} />;
  if (!contractor || (contractor.accountRole === 'homeowner') !== (source.kind === 'property')) return null;
  if (contractor.permissionLevel === 'read_only') return <Link className="text-sm text-tint" to="/parcels">View my parcel files</Link>;
  const unavailable = source.kind === 'property' && source.state.trim().toUpperCase() !== 'FL' ? 'Parcel watch is available for Florida properties.' :
    !source.graphParcelId && (!source.parcelId?.trim() || (source.kind === 'job' && !source.county?.trim())) ? 'A county/appraiser folio and county are required to link this record.' : null;
  if (unavailable) return <p className="text-sm text-muted">{unavailable}</p>;
  const needsCounty = source.kind === 'property' && !source.county && !source.graphParcelId;
  const accept = (row: ParcelMembership) => {
    if (source.kind === 'property' && row.occupying === false) {
      setOpen(false);
      return;
    }
    const target = membershipTarget(row);
    const correctSeat = source.kind === 'property' ? row.seat === 'homeowner' : ['prime', 'nested_gc', 'carpenter', 'electrician', 'roofer', 'abc'].includes(row.seat);
    if (!target || !correctSeat) throw new Error('An admitted parcel door is required to open this record.');
    setDestination(target);
  };
  return <div className="mt-4">
    <Button variant="secondary" onClick={() => setOpen(true)}>Open parcel {source.kind === 'property' ? 'watch' : 'file'}</Button>
    {open && <Modal title={source.kind === 'property' ? 'Open homeowner parcel watch' : 'Open contractor parcel file'} onClose={() => { if (!busy) setOpen(false); }}>
      <form className="space-y-4" onSubmit={async (event) => {
        event.preventDefault(); if (busy || (needsCounty && !county.trim())) return;
        setBusy(true); setError(null);
        try {
          const result = source.kind === 'property' ? await parcelApi.provisionProperty(source.id, needsCounty ? county.trim() : undefined) : await parcelApi.provisionJob(source.id, declared);
          if (result.membership.source?.id !== source.id || result.membership.source.kind !== source.kind) throw new Error('Parcel response does not match this record.');
          accept(result.membership);
        } catch (err) {
          // A failed response may follow a successful link. Read persisted state before any manual retry.
          try {
            const { memberships } = await parcelApi.list();
            const linked = memberships.filter((row) => (source.kind === 'property' ? row.propertyIds : row.jobIds)?.includes(source.id));
            if (linked.length === 1) accept(linked[0]);
          } catch { /* Keep the original error visible; never automatically repeat the write. */ }
          setError(err instanceof Error ? err.message : 'Could not open this parcel.');
        } finally { setBusy(false); }
      }}>
        <p className="text-sm text-muted">Open your admitted file, or create one when this record is eligible. An existing file requires admission for your account.</p>
        {needsCounty && <Field label="Property county" hint="Enter the Florida county for this tracked property."><Input required maxLength={50} value={county} onChange={(e) => setCounty(e.target.value)} /></Field>}
        {source.kind === 'job' && source.contractorRole === 'prime' && !source.graphParcelId &&
          <label className="flex items-start gap-3 text-sm"><input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} />I declare a direct contract with the owner (FL713_PRIVITY). Required to create a new prime file; reopening an admitted file needs no new declaration.</label>}
        {source.kind === 'job' && source.contractorRole !== 'prime' && <p className="text-sm text-muted">This job can link after you accept an invitation to its construction file.</p>}
        {error && <Banner title="Could not open parcel">{error} <Link to="/parcels" className="underline">Check my parcel files</Link></Banner>}
        <Button disabled={busy || (needsCounty && !county.trim())}>{busy ? 'Opening…' : 'Open or create parcel'}</Button>
      </form>
    </Modal>}
  </div>;
}
